import TreeItem from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem';
import DropdownDivider from '@/storybook/components/FormCollection/components/DropdownDivider/DropdownDivider';
import ListHeader from '@/storybook/components/FormCollection/components/ListHeader/ListHeader';
import { useFloatingLabelVirtualizedOptions } from '@/storybook/components/FormCollection/hooks/useFloatingLabelVirtualizedOptions';
import { VirtualRowMeta } from '@/storybook/components/FormCollection/hooks/useVirtualizedOptions';
import {
  createSelectItemKey,
  extractSelectItemLabelText,
  getPointerModifiersFromSyntheticEvent,
  getSelectItemLabelContent,
} from '@/storybook/components/FormCollection/utils/utils';
import { getCascadingItemBadge } from '@/storybook/components/FormCollection/utils/cascadingItemBadge';
import { SelectItem } from '@/types/ui';
import { useCallback, useMemo, type ChangeEvent, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../MultiSelectFloatingLabel.module.css';
import type { MultiSelectOptionClickModifiers } from './useMultiSelectHandlersFloatingLabel';

type UseMultiSelectVirtualizationProps<T> = {
  selectedOptions?: SelectItem<T>[];
  notSelectedOptions?: SelectItem<T>[];
  categoriesToDisplay: { category: string; index: number }[];
  noCategoryLabel: string;
  handleOptionClick: (
    clickedValue: T,
    currentScopedOptions: T[],
    index: number,
    modifiers: MultiSelectOptionClickModifiers,
  ) => void;
  isSelected: (value: T) => boolean;
};

type MultiSelectVirtualizedOptionRowProps<T> = {
  option: SelectItem<T>;
  isSelectedRow: boolean;
  optionValues: T[];
  indexWithinGroup: number;
  handleOptionClick: UseMultiSelectVirtualizationProps<T>['handleOptionClick'];
  isSelected: (value: T) => boolean;
  disabledTooltip: string;
};

const MultiSelectVirtualizedOptionRow = <T,>({
  option,
  isSelectedRow,
  optionValues,
  indexWithinGroup,
  handleOptionClick,
  isSelected,
  disabledTooltip,
}: MultiSelectVirtualizedOptionRowProps<T>) => {
  const { labelText, labelContent, optionKey } = useMemo(
    () => ({
      labelText: extractSelectItemLabelText(option),
      labelContent: getSelectItemLabelContent(option),
      optionKey: createSelectItemKey(option),
    }),
    [option],
  );

  const rowPrefix = isSelectedRow ? 'sortedSelectedOption' : 'sortedNotSelectedOption';
  const checkboxTestId = useMemo(() => `${rowPrefix}-${optionKey}`, [optionKey, rowPrefix]);
  const isDisabled = option.disabled === true;

  const handleRowClick = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      handleOptionClick(option.value, optionValues, indexWithinGroup, getPointerModifiersFromSyntheticEvent(e));
    },
    [handleOptionClick, indexWithinGroup, option.value, optionValues],
  );

  const handleCheck = useCallback(
    (e?: ChangeEvent<HTMLInputElement>) => {
      handleOptionClick(option.value, optionValues, indexWithinGroup, getPointerModifiersFromSyntheticEvent(e));
    },
    [handleOptionClick, indexWithinGroup, option.value, optionValues],
  );

  const checked = isSelected(option.value);
  const badge = getCascadingItemBadge(option, checked);

  return (
    <TreeItem
      className={!isSelectedRow ? styles.noSelect : undefined}
      label={labelText}
      labelContent={labelContent}
      tooltipText={isDisabled ? disabledTooltip : undefined}
      forceTooltip={isDisabled && checked}
      variant="flatList"
      level={0}
      showCheckbox
      checked={checked}
      onCheck={isDisabled ? undefined : handleCheck}
      onClick={isDisabled ? undefined : handleRowClick}
      isSelected={checked}
      isDisabled={isDisabled}
      badge={badge}
      data-testid={`${rowPrefix}Row-${optionKey}`}
      checkboxDataTestId={checkboxTestId}
    />
  );
};

export const useMultiSelectVirtualizationFloatingLabel = <T,>({
  selectedOptions,
  notSelectedOptions,
  categoriesToDisplay,
  noCategoryLabel,
  handleOptionClick,
  isSelected,
}: UseMultiSelectVirtualizationProps<T>) => {
  const { t } = useTranslation();
  const disabledTooltip = t('components.dropdownPanel.valueNoLongerApplicable');

  const {
    virtualizedList,
    parentRef,
    selectedOptions: resolvedSelected,
    notSelectedOptions: resolvedNotSelected,
  } = useFloatingLabelVirtualizedOptions<T>({
    selectedOptions,
    notSelectedOptions,
    categoriesToDisplay,
    noCategoryLabel,
  });

  const selectedOptionValues = resolvedSelected.map(o => o.value);
  const notSelectedOptionValues = resolvedNotSelected.map(o => o.value);

  const renderVirtualRow = useCallback(
    (vMeta: VirtualRowMeta<T>, virtualIndex?: number) => {
      if (vMeta.kind === 'divider') {
        return <DropdownDivider key={`divider-${virtualIndex}`} data-testid="multiselect-divider" />;
      }
      if (vMeta.kind === 'category' && vMeta.category) {
        return (
          <ListHeader
            key={`category-${vMeta.category}`}
            category={vMeta.category}
            isNoCategory={vMeta.isNoCategory}
            hideDivider={vMeta.isFirstCategory}
          />
        );
      }
      if (vMeta.option) {
        const { option, indexWithinGroup = 0 } = vMeta;
        if (option == null) {
          return null;
        }
        const isSelectedRow = vMeta.kind === 'selected';
        const optionValues = isSelectedRow ? selectedOptionValues : notSelectedOptionValues;
        return (
          <MultiSelectVirtualizedOptionRow<T>
            option={option}
            isSelectedRow={isSelectedRow}
            optionValues={optionValues}
            indexWithinGroup={indexWithinGroup}
            handleOptionClick={handleOptionClick}
            isSelected={isSelected}
            disabledTooltip={disabledTooltip}
          />
        );
      }
      return null;
    },
    [handleOptionClick, selectedOptionValues, notSelectedOptionValues, isSelected, disabledTooltip],
  );

  return {
    virtualizedList,
    parentRef,
    renderVirtualRow,
  };
};
